import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Dropdown } from 'react-bootstrap';
import { BiGitBranch } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { useCanEdit, READ_ONLY_TITLE } from 'Auth/permissions';
import { getLandscapeSummary, isTdmDisabled, startSubsetRun, errorText } from '../tdmApi';
import { isTdmLicenseError, NotLicensedPanel } from '../TdmLicenseGate';
import { engineBadge, rowsLabel, selectionLabel, budgetLabel, maskingLabel, fmtWhen, isMaskedSubset, parentOf, parseSeeds, RUN_STATUS_TONE } from '../tdmUtils';
import FiltersPanel from './FiltersPanel';
import { extractFacets, filterSubsets } from './landscapeFilters';
import RunStepStrip from '../RunStepStrip';
import usePolling from '../usePolling';

const POLL_MS = 15000;
const EMPTY_FILTERS = { query: '', data: [], engines: [], databases: [], keyIdsText: '' };

const Badges = ({ members }) => (
  <span className='tdm-badges'>
    {(members || []).map((m) => (
      <span key={m.id || m.dataSourceId || m.slug} className={`tdm-badge${m.parent ? ' tdm-badge--parent' : ''}`} title={`${m.dataSourceName}${m.parent ? ' (parent)' : ''}`}>
        {engineBadge(m.engine)}
      </span>
    ))}
  </span>
);

// The list says what it is for while it is empty — one sentence IS the flow (pull a subset, keep
// dated exports in staging, provision them into targets), and a first-time user cannot infer that
// from an empty list.
const ColumnIntro = ({ title, children, action }) => (
  <div className='tdm-intro' data-testid={`tdm-intro-${title}`}>
    <div className='tdm-intro__title'>{title}</div>
    <p className='tdm-intro__body'>{children}</p>
    {action}
  </div>
);

const StatusPill = ({ status }) => {
  if (!status) return null;
  return <span className={`tdm-pill tdm-pill--${RUN_STATUS_TONE[status] || 'neutral'}`}>{status}</span>;
};

/** "9/14 9:41 AM → 9/14 9:52 AM · SUCCEEDED · 441,800 rows · masked", or `never`. */
const LastExport = ({ subset }) => {
  const last = subset.lastRun;
  if (!last) return <span>never</span>;
  const rows = rowsLabel(last, subset.kind);
  return (
    <span>
      {fmtWhen(last.startedAt)}{last.finishedAt ? ` → ${fmtWhen(last.finishedAt)}` : ''}
      {' · '}<StatusPill status={last.status} />
      {rows ? ` · ${rows}` : ''}
      {' · '}{isMaskedSubset(subset) ? 'masked' : 'raw'}
      {last.provisionBlocker && (
        <span className='tdm-error tdm-card__blocker' data-testid={`tdm-subset-blocker-${subset.id}`}>
          cannot be deployed — {last.provisionBlocker}
        </span>
      )}
    </span>
  );
};

// The whole card opens the editor; Export subset and Deploy are the controls inside it that do
// something else, so they stop propagation and the key handler ignores events that bubbled up from
// them. Export subset starts a subset run (a new dated export in staging) — the same call as the
// editor's Actions → Run now; it is disabled while one is already running or starting.
const SubsetCard = ({ subset: s, onOpen, onExport, onProvision, exporting, exportError }) => {
  const last = s.lastRun;
  const running = Boolean(last && last.status === 'RUNNING');
  // An imported subset (read from a staging root's subset.json) has no source rows: it shows the
  // sources its file described, carries a pill, and cannot be exported from here — only provisioned.
  const imported = s.origin === 'IMPORTED';
  const canEdit = useCanEdit();
  const members = imported ? (s.importedSources || []) : (s.sources || []);
  const parent = imported ? (members.find((m) => m.parent) || null) : parentOf(s);
  const sourceNames = members.map((m) => `${m.dataSourceName}${m.parent ? ' (parent)' : ''}`).join(' · ');
  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      onOpen();
    }
  };
  const provisionTargets = s.lastProvision ? (s.lastProvision.targets || []).length : 0;
  return (
    <div className={`tdm-card tdm-card--clickable${running ? ' tdm-card--running' : ''}`} role='button' tabIndex={0}
      data-testid={`tdm-subset-${s.id}`} aria-label={`Open subset ${s.name}`} onClick={onOpen} onKeyDown={onKeyDown}>
      <div className='tdm-card__head'>
        <span className='tdm-card__name'>{s.name}</span>
        {imported && <span className='tdm-pill tdm-pill--info' data-testid={`tdm-subset-imported-${s.id}`} title={`Read from ${s.stagingName || 'staging'} — read-only here, not attached to data sources`}>Imported</span>}
        <Badges members={members} />
      </div>
      <div className='tdm-card__meta'>
        {s.description && (
          <>
            <span>Description</span>
            <span data-testid={`tdm-subset-description-${s.id}`}>{s.description}</span>
          </>
        )}
        <span>Sources</span>
        <span><Badges members={members} /> {sourceNames || '—'}</span>
        <span>Selection</span><span>{selectionLabel(s)} · {budgetLabel(s)}</span>
        <span>Parent</span><span>{parent ? parent.dataSourceName : '—'}</span>
        <span>Masking</span><span data-testid={`tdm-subset-masking-${s.id}`}>{maskingLabel(s)}</span>
        <span>Last export</span><span><LastExport subset={s} /></span>
        {s.lastProvision && (
          <>
            <span>Last deploy</span>
            <span>
              {fmtWhen(s.lastProvision.startedAt)} · <StatusPill status={s.lastProvision.status} />
              {` · ${provisionTargets} target${provisionTargets === 1 ? '' : 's'}`}
              {s.lastProvision.dryRun ? ' · dry run' : ''}
            </span>
          </>
        )}
      </div>
      {running && <div className='mt-2'><RunStepStrip steps={last.steps} compact /></div>}
      <div className='tdm-card__actions'>
        {!imported && <Button size='sm' className='pl-4 pr-4' data-testid={`tdm-subset-export-${s.id}`}
          title={canEdit ? 'Pull this subset into staging as a new dated export' : READ_ONLY_TITLE}
          disabled={running || exporting || !canEdit}
          onClick={(e) => { e.stopPropagation(); onExport(); }}>{exporting ? 'Starting…' : 'Export subset'}</Button>}
        <Button size='sm' className='pl-4 pr-4' data-testid={`tdm-subset-provision-${s.id}`}
          title='Deploy an export of this subset into target connections'
          onClick={(e) => { e.stopPropagation(); onProvision(); }}>Deploy</Button>
      </div>
      {exportError && <div className='tdm-error mt-2' data-testid={`tdm-subset-export-error-${s.id}`}>{exportError}</div>}
    </div>
  );
};

// Test Data Landscape: one list of subsets — each knows its sources, its selection and where it
// exports to. One summary call, polled while anything runs.
const Landscape = () => {
  const history = useHistory();
  const { connections, isReadOnly } = useContext(AppContext);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [licenseError, setLicenseError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [exporting, setExporting] = useState(null);
  const [exportErrors, setExportErrors] = useState({});

  const load = useCallback(async () => {
    try {
      const s = await getLandscapeSummary(30);
      setSummary(s);
      setError(null);
    } catch (e) {
      if (isTdmLicenseError(e)) { setLicenseError(e.body); return; }
      setError(isTdmDisabled(e) ? 'Test Data Management is not enabled on this deployment.' : (e?.message || 'Could not load the landscape'));
    } finally {
      setLoading(false);
    }
  }, []);
  const running = Boolean(summary && summary.counts && summary.counts.runningRuns > 0);
  usePolling(load, running ? POLL_MS : 60000, true);

  const subsets = useMemo(() => (summary && summary.subsets) || [], [summary]);
  const connById = useMemo(() => Object.fromEntries((connections || []).map((c) => [String(c.id), c])), [connections]);
  const facets = useMemo(() => extractFacets(subsets, connById), [subsets, connById]);
  const shown = useMemo(
    () => filterSubsets(subsets, { ...filters, keyIds: parseSeeds(filters.keyIdsText) }, connById),
    [subsets, filters, connById],
  );

  // Reloading the summary picks up the new RUNNING export, which also switches polling to POLL_MS.
  const exportSubset = async (id) => {
    setExporting(id);
    setExportErrors((errs) => ({ ...errs, [id]: null }));
    try {
      await startSubsetRun(id);
      await load();
    } catch (e) {
      setExportErrors((errs) => ({ ...errs, [id]: errorText(e, 'Export could not start') }));
    } finally {
      setExporting(null);
    }
  };

  const newSubset = () => history.push('/tdm/subsets/new');
  const clearFacets = () => setFilters((f) => ({ ...EMPTY_FILTERS, query: f.query }));
  const clearAll = () => setFilters(EMPTY_FILTERS);

  if (licenseError) return <NotLicensedPanel expired={licenseError.error === 'LICENSE_EXPIRED'} />;

  const title = shown.length === subsets.length ? `Subsets · ${subsets.length}` : `Subsets · ${shown.length}/${subsets.length}`;

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='tdm-page tdm-page--wide'>
        <PageHeader
          icon={BiGitBranch}
          eyebrow='Test Data Management'
          header='Test Data Landscape'
          tooltip='Subsets pull data from their sources into staging; deploying loads an export into targets'
          meta={<span>A subset pulls from its sources into staging; deploying loads an export into targets.</span>}
          primaryAction={<Button size='sm' onClick={newSubset} disabled={!!isReadOnly} data-testid='tdm-new-subset'>New subset</Button>}
          actions={[
            <Dropdown.Item key='target-groups' data-testid='tdm-action-target-groups' onClick={() => history.push('/tdm/target-groups')}>
              Target groups
            </Dropdown.Item>,
            <Dropdown.Item key='staging' data-testid='tdm-action-staging' onClick={() => history.push('/tdm/staging')}>
              Staging
            </Dropdown.Item>,
          ]}
        />

        {subsets.length === 0 ? (
          <div className='tdm-subset-list'>
            <ColumnIntro title='Subsets'
              action={<Button size='sm' variant='outline-primary' onClick={newSubset} disabled={!!isReadOnly} data-testid='tdm-intro-new-subset'>New subset</Button>}>
              Pull a subset from one or more sources, keep dated exports in staging, deploy them into targets.
            </ColumnIntro>
          </div>
        ) : (
          <>
            <input type='search' className='tdm-search tdm-subset-search' data-testid='tdm-search' value={filters.query}
              placeholder='Search subsets by name, description, source, table or where clause'
              aria-label='Search subsets'
              onChange={(e) => setFilters({ ...filters, query: e.target.value })} />
            <FiltersPanel filters={filters} facets={facets} onChange={setFilters} onClear={clearFacets} />

            <div className='tdm-column__title tdm-subset-list__title'><span>{title}</span></div>
            <div className='tdm-subset-list'>
              {shown.length === 0 && (
                <div className='tdm-empty' data-testid='tdm-no-match'>
                  No subsets match the search and filters.{' '}
                  <button type='button' className='tdm-link' onClick={clearAll}>Clear filters</button>
                </div>
              )}
              {shown.map((s) => (
                <SubsetCard key={s.id} subset={s}
                  onOpen={() => history.push(`/tdm/subsets/${s.id}`)}
                  onExport={() => exportSubset(s.id)}
                  exporting={exporting === s.id}
                  exportError={exportErrors[s.id]}
                  onProvision={() => history.push(`/tdm/subsets/${s.id}/provision`)} />
              ))}
            </div>
          </>
        )}
      </div>
    </FetchContainer>
  );
};

export default Landscape;
